import {CheckOutlined, CloseOutlined} from '@ant-design/icons';
import {Button, Card, Form as AntdForm, Select as AntdSelect, Space} from 'antd';
import {
  CreateProviderInput,
  Maybe,
  MetadataConfigItem,
  MetadataItem,
  MetadataItemType,
  PaymentMethods,
  useAvailablePaymentMethodsQuery,
  useAvailableProcessorsQuery,
  useProviderConfigQuery,
  useProvidersQuery
} from 'api/generated';
import {BottomActions, CancelButton, Spacer} from 'components';
import dot from 'dot-object';
import {Formik, FormikProps} from 'formik';
import {Checkbox, Form, FormItem, Input, InputNumber, Select, Switch} from 'formik-antd';
import {acquirerLabels, paymentMethodLabels, paymentProcessorLabels} from 'lib/labels';
import {omitEmpty} from 'lib/omitEmpty';
import {isDefined, parseJSON} from 'lib/utils';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import set from 'set-value';
import {v4} from 'uuid';
import * as yup from 'yup';

dot.keepArray = true;

export type FormValues = Omit<Partial<CreateProviderInput>, 'metadata'> & {
  accountId: string;
  acquirer: string;
  metadata: Record<string, any>;
};

type Props = {
  initialValues: FormValues;
  isNew: boolean;
  isLoading: boolean;
  accountId: string;
  onSubmit: (input: any) => Promise<void>;
};

const PAYMENT_METHODS = [
  PaymentMethods.CARD,
  PaymentMethods.PAYPAL,
  PaymentMethods.BIZUM,
  PaymentMethods.ALIPAY,
  PaymentMethods.MBWAY,
  PaymentMethods.MULTIBANCO,
  PaymentMethods.SEPA,
  PaymentMethods.TRUSTLY,
  PaymentMethods.BANCONTACT,
  PaymentMethods.KLARNA,
  PaymentMethods.GIROPAY,
  PaymentMethods.IDEAL,
  PaymentMethods.SOFORT,
  PaymentMethods.EPS,
];

export const parseValue = (value: any, type?: MetadataItemType) => {
  if (isDefined(value) && type && type !== MetadataItemType.STRING) {
    return parseJSON(value);
  }
  return value;
};

export const stringifyValue = (value: any, type?: MetadataItemType) => {
  if (isDefined(value) && type && type !== MetadataItemType.STRING) {
    return JSON.stringify(value);
  }
  return value;
};

export const metaToObject = (metadata: Array<MetadataItem> | null | undefined) => {
  if (!metadata) return {};
  return metadata.reduce<Record<string, any>>((result, item) => {
    set(result, item.key, parseValue(item.value, item.type));
    return result;
  }, {});
};

export const metaToArray = (metadata: Record<string, any>, config: MetadataConfigItem[]) => {
  const flatMeta = dot.dot(metadata);
  const configByKey = config.reduce<Record<string, MetadataConfigItem>>((result, item) => {
    result[item.key] = item;
    return result;
  }, {});
  return Object.keys(flatMeta).map((key) => ({
    key,
    value: stringifyValue(flatMeta[key], configByKey[key]?.type)
  }));
};

const sortMetadata = (a: Maybe<MetadataConfigItem>, b: Maybe<MetadataConfigItem>) => {
  if (!a || !b) return 0;
  return b.type.localeCompare(a.type);
};

const validationSchema = yup.object().shape({
  processor: yup.string().required().nullable(),
  paymentMethod: yup.string().required(),
  acquirer: yup.string().required().nullable(),
  rules: yup.object().shape({
    minAmount: yup.number().min(0).nullable(),
    maxAmount: yup.number().min(0).nullable(),
    shopName: yup.string().nullable(),
    descriptionName: yup.string().nullable()
  })
});

export const EditProviderForm: React.FC<Props> = ({
  onSubmit,
  initialValues,
  isLoading,
  isNew,
  accountId
}) => {
  const [paymentMethod, setPaymentMethod] = useState(initialValues.paymentMethod);
  const [processor, setProcessor] = useState(initialValues.processor);
  const [isUpdating, setIsUpdating] = useState(false);
  const ref = useRef<FormikProps<typeof initialValues>>(null);
  const {loading: loadingPaymentMethods} = useAvailablePaymentMethodsQuery({
    variables: {accountId}
  });
  const {data: processorsData, loading: loadingProcessors} = useAvailableProcessorsQuery({
    variables: {accountId, paymentMethod: paymentMethod!}
  });
  const {data: providersData, loading: loadingProviders} = useProvidersQuery({
    variables: {accountId}
  });
  const {data: configData, loading: loadingConfig} = useProviderConfigQuery({
    variables: {paymentMethod: paymentMethod!, processor: processor!},
    skip: !processor
  });

  const availableProcessors = processorsData?.availableProcessors || [];
  const providers = providersData?.providers || [];

  const metadataConfig = useMemo(
    () => [...(configData?.providerConfig?.metadataConfig || [])].sort(sortMetadata),
    [configData?.providerConfig?.metadataConfig]
  );
  const config = configData?.providerConfig;

  useEffect(() => {
    if (!ref.current) return;
    const defaultMetadata = metadataConfig.reduce<Record<string, any>>((result, item) => {
      result[item.key] = parseValue(item.default, item.type);
      return result;
    }, {});
    const values = ref.current.values;
    const newValues = {
      ...initialValues,
      ...omitEmpty(values),
      metadata: {
        ...dot.object(defaultMetadata),
        ...omitEmpty(initialValues.metadata),
        ...omitEmpty(values.metadata)
      }
    };
    ref.current.resetForm({values: newValues});
  }, [initialValues, metadataConfig]);

  useEffect(() => {
    setProcessor(initialValues.processor);
  }, [initialValues.processor]);

  useEffect(() => {
    setPaymentMethod(initialValues.paymentMethod);
  }, [initialValues.paymentMethod]);

  const handleSubmit = async (values: FormValues) => {
    setIsUpdating(true);
    const update = {...values, metadata: metaToArray(values.metadata, metadataConfig)};
    try {
      await onSubmit(update);
    } finally {
      setIsUpdating(false);
    }
  };

  const renderInput = (item: Maybe<MetadataConfigItem>) => {
    if (!item) return null;
    const name = `metadata.${item.key}`;
    const placeholder = item.example || undefined;
    let input;
    if (item.possibleValues) {
      input = (
        <Select
          name={name}
          mode={item.type === MetadataItemType.STRING_ARRAY ? 'multiple' : undefined}>
          {item.possibleValues.map((value) => (
            <Select.Option key={value} value={value}>
              {value}
            </Select.Option>
          ))}
        </Select>
      );
    } else if (item.type === MetadataItemType.BOOLEAN) {
      input = <Checkbox name={name}>{item.label}</Checkbox>;
    } else if (item.type === MetadataItemType.NUMBER) {
      input = <InputNumber name={name} placeholder={placeholder} />;
    } else if (item.secret) {
      input = <Input.Password name={name} autoComplete={v4()} placeholder={placeholder} />;
    } else {
      input = <Input name={name} autoComplete={v4()} placeholder={placeholder} />;
    }

    const validate = (value: any) => {
      if (!item.regex) return;
      if (!new RegExp(item.regex).test(value)) {
        return `${item.label} has invalid format`;
      }
    };

    return (
      <FormItem
        key={name}
        name={name}
        validate={validate}
        label={item.type !== MetadataItemType.BOOLEAN ? item.label : null}>
        {input}
      </FormItem>
    );
  };

  useEffect(() => {
    if (ref.current && isNew && config?.acquirers) {
      const acquirer = config.acquirers.length === 1 ? config?.acquirers[0] : null;
      ref.current.setFieldValue('acquirer', acquirer);
    }
  }, [config]);

  useEffect(() => {
    if (ref.current && isNew && availableProcessors.length === 1) {
      const processor = availableProcessors[0].processor;
      setProcessor(processor);
      ref.current.setFieldValue('processor', availableProcessors[0].processor);
    }
  }, [availableProcessors]);

  const loading = loadingConfig || isLoading;

  return (
    <Formik
      innerRef={ref}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}>
      {({setFieldValue, values, errors, submitForm}) => {
        const providersToCopy = providers.filter((p) => p.processor === values.processor);
        return (
          <Form autoComplete="off" layout="vertical">
            <Card title="Settings" loading={isLoading} style={{marginBottom: 20}}>
              <FormItem name="paymentMethod" label="Payment method">
                <Select
                  showSearch
                  loading={loadingPaymentMethods}
                  disabled={!isNew}
                  name="paymentMethod"
                  onChange={(value) => {
                    setPaymentMethod(value);
                    setProcessor(null);
                    setFieldValue('paymentMethod', value);
                    setFieldValue('processor', null);
                    setFieldValue('metadata', {});
                  }}>
                  {PAYMENT_METHODS.map((paymentMethod) => (
                    <Select.Option key={paymentMethod} value={paymentMethod}>
                      {paymentMethodLabels[paymentMethod]}
                    </Select.Option>
                  ))}
                </Select>
              </FormItem>
              <FormItem name="processor" label="Payment processor">
                <Select
                  name="processor"
                  placeholder="Select payment processor"
                  loading={loadingProcessors}
                  disabled={loadingPaymentMethods || !isNew}
                  onChange={(value) => {
                    setProcessor(value);
                    setFieldValue('processor', value);
                    setFieldValue('metadata', {});
                  }}>
                  {availableProcessors.map(({processor}) => (
                    <Select.Option key={processor!} value={processor!}>
                      {paymentProcessorLabels[processor!]}
                    </Select.Option>
                  ))}
                </Select>
              </FormItem>
              {(config || !isNew) && (
                <>
                  <FormItem name="acquirer" label="Acquirer">
                    <Select name="acquirer" placeholder="Select the acquirer" showSearch>
                      {config?.acquirers?.map((acquirer) => (
                        <Select.Option key={acquirer} value={acquirer}>
                          {acquirerLabels[acquirer]}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>
                  <FormItem
                    name="nativeHostedPayment"
                    help={errors.nativeHostedPayment ? errors.nativeHostedPayment : null}
                    extra="Change this only if you're sure what you're doing. Enable this option if payment method is using redirect to the payment page hosted by the provider (for example Bizum).">
                    <Checkbox name="nativeHostedPayment">Provider's native payment page</Checkbox>
                  </FormItem>
                  <FormItem name="enabled">
                    <Space>
                      <Switch
                        //@ts-ignore
                        id="enabled"
                        name="enabled"
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                      />
                      <label htmlFor="enabled">Enabled</label>
                    </Space>
                  </FormItem>
                  <FormItem name="test">
                    <Space>
                      <Switch
                        //@ts-ignore
                        id="test"
                        name="test"
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                      />
                      <label htmlFor="test">TEST Mode</label>
                    </Space>
                  </FormItem>
                </>
              )}
            </Card>
            {(config || !isNew) && (
              <Card title="Payment method settings" loading={loading} style={{marginBottom: 20}}>
                {isNew && providersToCopy.length > 0 && (
                  <AntdForm.Item label="Copy settings from existing payment method">
                    <AntdSelect
                      loading={loadingProviders}
                      onChange={(providerId) => {
                        const provider = providers.find((p) => p.id === providerId);
                        if (provider && provider.metadata) {
                          const metadata = provider.metadata.filter((m) =>
                            metadataConfig.find((c) => c.key === m.key)
                          );
                          setFieldValue('metadata', metaToObject(metadata));
                          setFieldValue('nativeHostedPayment', provider.nativeHostedPayment);
                          setFieldValue('test', provider.test);
                        }
                      }}>
                      {providersToCopy.map((provider) => (
                        <Select.Option key={provider.id} value={provider.id}>
                          {provider.processor} - {paymentMethodLabels[provider.paymentMethod!]}
                        </Select.Option>
                      ))}
                    </AntdSelect>
                  </AntdForm.Item>
                )}
                {metadataConfig.map(renderInput)}
              </Card>
            )}
            {config && (
              <Card title="Routing rules">
                <FormItem name="rules.minAmount" label="Minimum payment amount">
                  <InputNumber
                    name="rules.minAmount"
                    type="number"
                    style={{width: '100%'}}
                    precision={2}
                    addonAfter="EUR"
                  />
                </FormItem>
                <FormItem name="rules.maxAmount" label="Maximum payment amount">
                  <InputNumber
                    name="rules.maxAmount"
                    type="number"
                    style={{width: '100%'}}
                    precision={2}
                    addonAfter="EUR"
                  />
                </FormItem>
                <FormItem name="rules.shopNameLike" label="The name of the store matches">
                  <Input name="rules.shopNameLike" placeholder="My company" />
                </FormItem>
                <FormItem name="rules.descriptionLike" label="The payment description matches">
                  <Input name="rules.descriptionLike" placeholder="My description" />
                </FormItem>
              </Card>
            )}
            <BottomActions>
              <CancelButton backPath={`/accounts/${accountId}?tab=payment-methods`} />
              <Spacer />
              <Button
                onClick={submitForm}
                type="primary"
                loading={isUpdating && isNew}
                disabled={isUpdating}>
                {isNew ? 'Add payment method' : 'Update payment method'}
              </Button>
            </BottomActions>
          </Form>
        );
      }}
    </Formik>
  );
};
